import {
  ConnectorType,
  EvType,
  EvseOperationalState,
  EvseStatus,
  HealthGrade,
  InfrastructureOverviewMicrogridControllerStatus,
  OcppChargePointStatus,
  OverviewAssetCategory,
  SiteType,
  VehicleDetailsAlert,
  VehicleDetailsWarning,
  VehicleEvseCombinedState,
  VehicleOperationalStatus,
  Weekday,
} from '@core/api/single-site/single-site.enums';

import {
  ConnectorTypeStoreModelEnum,
  EvTypeStoreModelEnum,
  EvseOperationalStateStoreModelEnum,
  EvseStatusStoreModelEnum,
  HealthGradeStoreModelEnum,
  InfrastructureOverviewMicrogridControllerStatusStoreModelEnum,
  OcppChargePointStatusStoreModelEnum,
  OverviewAssetCategoryStoreModelEnum,
  SiteTypeStoreModelEnum,
  VehicleDetailsAlertStoreModelEnum,
  VehicleDetailsWarningStoreModelEnum,
  VehicleEvseCombinedStateStoreModelEnum,
  VehicleOperationalStatusStoreModelEnum,
} from './single-site.enums';

export const mapOverviewAssetCategoryApiModelEnumToStoreModelEnum = (
  assetCategory: OverviewAssetCategory
): OverviewAssetCategoryStoreModelEnum => {
  return {
    [OverviewAssetCategory.EvLoad]: OverviewAssetCategoryStoreModelEnum.EvLoad,
    [OverviewAssetCategory.BaseLoad]: OverviewAssetCategoryStoreModelEnum.BaseLoad,
    [OverviewAssetCategory.Pv]: OverviewAssetCategoryStoreModelEnum.Pv,
    [OverviewAssetCategory.GridLoad]: OverviewAssetCategoryStoreModelEnum.GridLoad,
    [OverviewAssetCategory.Battery]: OverviewAssetCategoryStoreModelEnum.Battery,
  }[assetCategory];
};

export const mapInfrastructureOverviewMicrogridControllerStatusApiModelEnumToStoreModelEnum = (
  status: InfrastructureOverviewMicrogridControllerStatus
): InfrastructureOverviewMicrogridControllerStatusStoreModelEnum => {
  return {
    [InfrastructureOverviewMicrogridControllerStatus.NotApplicable]:
      InfrastructureOverviewMicrogridControllerStatusStoreModelEnum.NotApplicable,
    [InfrastructureOverviewMicrogridControllerStatus.Online]: InfrastructureOverviewMicrogridControllerStatusStoreModelEnum.Online,
    [InfrastructureOverviewMicrogridControllerStatus.Offline]: InfrastructureOverviewMicrogridControllerStatusStoreModelEnum.Offline,
  }[status];
};

export const mapVehicleOperationalStatusApiModelEnumToStoreModelEnum = (
  status: VehicleOperationalStatus
): VehicleOperationalStatusStoreModelEnum => {
  return {
    [VehicleOperationalStatus.Ready]: VehicleOperationalStatusStoreModelEnum.Ready,
    [VehicleOperationalStatus.Charging]: VehicleOperationalStatusStoreModelEnum.Charging,
    [VehicleOperationalStatus.Queued]: VehicleOperationalStatusStoreModelEnum.Queued,
    [VehicleOperationalStatus.Faulted]: VehicleOperationalStatusStoreModelEnum.Faulted,
    [VehicleOperationalStatus.InDepot]: VehicleOperationalStatusStoreModelEnum.InDepot,
    [VehicleOperationalStatus.OnTheRoad]: VehicleOperationalStatusStoreModelEnum.OnTheRoad,
    [VehicleOperationalStatus.Unknown]: VehicleOperationalStatusStoreModelEnum.Unknown,
  }[status];
};

export const mapVehicleOperationalStatusStoreModelEnumToApiModelEnum = (
  status: VehicleOperationalStatusStoreModelEnum
): VehicleOperationalStatus => {
  return {
    [VehicleOperationalStatusStoreModelEnum.Ready]: VehicleOperationalStatus.Ready,
    [VehicleOperationalStatusStoreModelEnum.Charging]: VehicleOperationalStatus.Charging,
    [VehicleOperationalStatusStoreModelEnum.Queued]: VehicleOperationalStatus.Queued,
    [VehicleOperationalStatusStoreModelEnum.Faulted]: VehicleOperationalStatus.Faulted,
    [VehicleOperationalStatusStoreModelEnum.InDepot]: VehicleOperationalStatus.InDepot,
    [VehicleOperationalStatusStoreModelEnum.OnTheRoad]: VehicleOperationalStatus.OnTheRoad,
    [VehicleOperationalStatusStoreModelEnum.Unknown]: VehicleOperationalStatus.Unknown,
  }[status];
};

export const mapVehicleEvseCombinedStateApiModelEnumToStoreModelEnum = (
  status: VehicleEvseCombinedState
): VehicleEvseCombinedStateStoreModelEnum => {
  return {
    [VehicleEvseCombinedState.Available]: VehicleEvseCombinedStateStoreModelEnum.Available,
    [VehicleEvseCombinedState.Charging]: VehicleEvseCombinedStateStoreModelEnum.Charging,
    [VehicleEvseCombinedState.Queued]: VehicleEvseCombinedStateStoreModelEnum.Queued,
    [VehicleEvseCombinedState.Ready]: VehicleEvseCombinedStateStoreModelEnum.Ready,
    [VehicleEvseCombinedState.Faulted]: VehicleEvseCombinedStateStoreModelEnum.Faulted,
    [VehicleEvseCombinedState.FaultedUnplugRequired]: VehicleEvseCombinedStateStoreModelEnum.FaultedUnplugRequired,
    [VehicleEvseCombinedState.Maintenance]: VehicleEvseCombinedStateStoreModelEnum.Maintenance,
  }[status];
};

export const mapHealthGradeApiModelEnumToStoreModelEnum = (healthGrade: HealthGrade): HealthGradeStoreModelEnum => {
  return {
    [HealthGrade.Good]: HealthGradeStoreModelEnum.Good,
    [HealthGrade.Warning]: HealthGradeStoreModelEnum.Warning,
    [HealthGrade.Error]: HealthGradeStoreModelEnum.Error,
  }[healthGrade];
};

export const mapVehicleDetailsWarningApiModelEnumToStoreModelEnum = (
  warning: VehicleDetailsWarning
): VehicleDetailsWarningStoreModelEnum => {
  return {
    [VehicleDetailsWarning.LateArrival]: VehicleDetailsWarningStoreModelEnum.LateArrival,
    [VehicleDetailsWarning.PluginAsap]: VehicleDetailsWarningStoreModelEnum.PluginAsap,
  }[warning];
};

export const mapVehicleDetailsAlertApiModelEnumToStoreModelEnum = (alert: VehicleDetailsAlert): VehicleDetailsAlertStoreModelEnum => {
  return {
    [VehicleDetailsAlert.ChargerNotRamping]: VehicleDetailsAlertStoreModelEnum.ChargerNotRamping,
    [VehicleDetailsAlert.ContactorError]: VehicleDetailsAlertStoreModelEnum.ContactorError,
    [VehicleDetailsAlert.PluginOverdue]: VehicleDetailsAlertStoreModelEnum.PluginOverdue,
  }[alert];
};

export const mapVehicleDetailsAlertStoreModelEnumToApiModelEnum = (alert: VehicleDetailsAlertStoreModelEnum): VehicleDetailsAlert => {
  return {
    [VehicleDetailsAlertStoreModelEnum.ChargerNotRamping]: VehicleDetailsAlert.ChargerNotRamping,
    [VehicleDetailsAlertStoreModelEnum.ContactorError]: VehicleDetailsAlert.ContactorError,
    [VehicleDetailsAlertStoreModelEnum.PluginOverdue]: VehicleDetailsAlert.PluginOverdue,
  }[alert];
};

export const mapEvTypeApiModelEnumToStoreModelEnum = (evType: EvType): EvTypeStoreModelEnum => {
  return {
    [EvType.Van]: EvTypeStoreModelEnum.Van,
    [EvType.Bus]: EvTypeStoreModelEnum.Bus,
    [EvType.Mixed]: EvTypeStoreModelEnum.Mixed,
  }[evType];
};

export const mapSiteTypeApiModelEnumToStoreModelEnum = (siteType: SiteType): SiteTypeStoreModelEnum => {
  return {
    [SiteType.Fleet]: SiteTypeStoreModelEnum.Fleet,
    [SiteType.Public]: SiteTypeStoreModelEnum.Public,
  }[siteType];
};

export const mapEvseStatusApiModelEnumToStoreModelEnum = (status: EvseStatus): EvseStatusStoreModelEnum => {
  return {
    [EvseStatus.Charging]: EvseStatusStoreModelEnum.Charging,
    [EvseStatus.Queued]: EvseStatusStoreModelEnum.Queued,
    [EvseStatus.Available]: EvseStatusStoreModelEnum.Available,
    [EvseStatus.Faulted]: EvseStatusStoreModelEnum.Faulted,
    [EvseStatus.Maintenance]: EvseStatusStoreModelEnum.Maintenance,
    [EvseStatus.Unknown]: EvseStatusStoreModelEnum.Unknown,
  }[status];
};

export const mapWeekdayToNumber = (weekday: Weekday): number => {
  return {
    [Weekday.Monday]: 1,
    [Weekday.Tuesday]: 2,
    [Weekday.Wednesday]: 3,
    [Weekday.Thursday]: 4,
    [Weekday.Friday]: 5,
    [Weekday.Saturday]: 6,
    [Weekday.Sunday]: 7,
  }[weekday];
};

export const mapWeekdayNumberToWeekday = (weekday: number): Weekday => {
  return {
    [1]: Weekday.Monday,
    [2]: Weekday.Tuesday,
    [3]: Weekday.Wednesday,
    [4]: Weekday.Thursday,
    [5]: Weekday.Friday,
    [6]: Weekday.Saturday,
    [7]: Weekday.Sunday,
  }[weekday]!;
};

export const mapConnectorTypeApiModelEnumToStoreModelEnum = (connectorType: ConnectorType): ConnectorTypeStoreModelEnum => {
  return {
    [ConnectorType.CCS2]: ConnectorTypeStoreModelEnum.Ccs2,
    [ConnectorType.TYPE_2]: ConnectorTypeStoreModelEnum.Type2,
    [ConnectorType.CHADEMO]: ConnectorTypeStoreModelEnum.Chademo,
    [ConnectorType.NACS]: ConnectorTypeStoreModelEnum.Nacs,
    [ConnectorType.MCS]: ConnectorTypeStoreModelEnum.Mcs,
  }[connectorType];
};

export const mapOcppChargePointStatusApiModelEnumToStoreModelEnum = (
  status: OcppChargePointStatus
): OcppChargePointStatusStoreModelEnum => {
  return {
    [OcppChargePointStatus.Available]: OcppChargePointStatusStoreModelEnum.Available,
    [OcppChargePointStatus.Occupied]: OcppChargePointStatusStoreModelEnum.Occupied,
    [OcppChargePointStatus.Preparing]: OcppChargePointStatusStoreModelEnum.Preparing,
    [OcppChargePointStatus.Charging]: OcppChargePointStatusStoreModelEnum.Charging,
    [OcppChargePointStatus.SuspendedEvse]: OcppChargePointStatusStoreModelEnum.SuspendedEvse,
    [OcppChargePointStatus.SuspendedEv]: OcppChargePointStatusStoreModelEnum.SuspendedEv,
    [OcppChargePointStatus.Finishing]: OcppChargePointStatusStoreModelEnum.Finishing,
    [OcppChargePointStatus.Reserved]: OcppChargePointStatusStoreModelEnum.Reserved,
    [OcppChargePointStatus.Unavailable]: OcppChargePointStatusStoreModelEnum.Unavailable,
    [OcppChargePointStatus.Faulted]: OcppChargePointStatusStoreModelEnum.Faulted,
  }[status];
};

export const mapEvseOperationalStateApiModelEnumToStoreModelEnum = (status: EvseOperationalState): EvseOperationalStateStoreModelEnum => {
  return {
    [EvseOperationalState.Available]: EvseOperationalStateStoreModelEnum.Available,
    [EvseOperationalState.Charging]: EvseOperationalStateStoreModelEnum.Charging,
    [EvseOperationalState.Queued]: EvseOperationalStateStoreModelEnum.Queued,
    [EvseOperationalState.Faulted]: EvseOperationalStateStoreModelEnum.Faulted,
    [EvseOperationalState.FaultedUnplugRequired]: EvseOperationalStateStoreModelEnum.FaultedUnplugRequired,
    [EvseOperationalState.Maintenance]: EvseOperationalStateStoreModelEnum.Maintenance,
  }[status];
};
